// equity/ci.ts - Monte-Carlo confidence-interval math. ENGINE_DESIGN §5 ("reports {equity, ci95}").
//
// Each MC trial yields an outcome in [0,1]: heads-up it is 1 (hero wins), 0.5 (chop), or 0 (loss);
// multiway it is the pot share 1/k when hero is one of k winners, else 0. Equity is the sample mean
// of those outcomes. Because the outcome is NOT Bernoulli (0.5 and fractional shares occur), we use
// the general sample-variance estimator, not the p(1−p) binomial shortcut - the binomial form would
// UNDER-state the variance of a three-valued {0,0.5,1} outcome and hand back too-tight intervals.
//
// For n i.i.d. trials with sample mean m and sample variance s² (Bessel-corrected):
//   s²      = (Σx² − n·m²) / (n − 1)
//   SE(m)   = sqrt(s² / n)
//   ci95    = z · SE(m),   z = 1.959963985 (two-sided 95% normal quantile)
// The CLT applies at our iteration counts (thousands), so the normal quantile is appropriate; the
// facts layer buckets equity anyway, so sub-bucket MC noise is invisible downstream (ENGINE_DESIGN §5).
//
// An accumulator keeps Σx and Σx² in doubles so a provider can stream trials without retaining them.
/** Two-sided 95% standard-normal quantile (Φ⁻¹(0.975)). */
export const Z95 = 1.959963984540054;
/** Streaming accumulator for MC outcomes in [0,1]. Tracks n, Σx, Σx² in double precision. */
export class MeanAccumulator {
    n = 0;
    sumX = 0;
    sumX2 = 0;
    /** Record one trial outcome (expected in [0,1]; not enforced on the hot path). */
    push(x) {
        this.n += 1;
        this.sumX += x;
        this.sumX2 += x * x;
    }
    /** Sample mean (0 when no trials yet). */
    mean() {
        return this.n === 0 ? 0 : this.sumX / this.n;
    }
    /** Bessel-corrected sample variance. 0 for n < 2 (undefined spread → treat as 0). */
    variance() {
        if (this.n < 2)
            return 0;
        // (Σx² − n·m²)/(n−1), floored at 0 against tiny negative round-off.
        const m = this.sumX / this.n;
        const v = (this.sumX2 - this.n * m * m) / (this.n - 1);
        return v > 0 ? v : 0;
    }
    /** 95% CI half-width for the mean: z·sqrt(s²/n). 0 for n < 2. */
    ci95() {
        if (this.n < 2)
            return 0;
        return Z95 * Math.sqrt(this.variance() / this.n);
    }
}
/** 95% CI half-width from raw sums, for callers that keep their own accumulators. */
export const ci95HalfWidth = (sumX, sumX2, n) => {
    if (n < 2)
        return 0;
    const m = sumX / n;
    const v = (sumX2 - n * m * m) / (n - 1);
    return Z95 * Math.sqrt((v > 0 ? v : 0) / n);
};
/** Combine two independent CI half-widths (e.g. estimate vs oracle) in quadrature. Used by the
 *  oracle test's "MC within 3× combined CI" check. */
export const combineCi = (a, b) => Math.sqrt(a * a + b * b);
//# sourceMappingURL=ci.js.map